/**
 * Build calendar grid cells with optional 2-column double-box spans.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * REGRESSION LOCK — calendar double-box MUST cover two dates as one cell
 * ═══════════════════════════════════════════════════════════════════════════
 * Product rule (all calendar sets — set1 / set2 / set3):
 *   A double-box anchor always visually occupies **two columns** whenever the
 *   immediate next calendar day sits in the **same week row**. Label = `D–D+1 ×2`.
 *
 * Pairing is driven by the calendar day only — NOT by `availableDates`,
 * `consumed_by_double_box`, day-off, paused, or frozen.
 *
 * Why this keeps getting broken:
 *   Agents reintroduce `consumed_by_double_box.has(next) || isDayOff` as a
 *   gate for `col_span: 2`. That leaves orphan single cells like `22 ×2`
 *   beside a free `23` whenever the next day is outside `availableDates`
 *   (menu cutoff / pause / plain gap) but still rendered in the month grid.
 *
 * Domain lock (`getDeliveryDatesConsumedByDoubleBox`) stays separate: it only
 * decides which dates are non-selectable for menu. It must never decide whether
 * the grid merges columns.
 *
 * Only skip the 2-column span when the next calendar day is missing from this
 * month grid or would wrap to the next week row (still show ×2 on the anchor).
 *
 * See `.cursor/rules/calendar-double-box-span.mdc`.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import moment from 'moment';
import type { CalendarDay } from '../../data/domains/calendar';
import type { CalendarDateCellVisual } from './calendarDateCellVisuals';
import type { CalendarGridCellData } from './CalendarGrid';

export type EnrichCalendarDayInput = {
  day: CalendarDay;
  index: number;
  visual: CalendarDateCellVisual;
  disabled: boolean;
};

function nextCalendarYmd(full_date: string): string | null {
  const m = moment(full_date, 'YYYY-MM-DD', true).add(1, 'day');
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/**
 * @param consumed_by_double_box Dates reserved for box 2 (interaction lock only —
 *   used to disable orphan follow-ups that could not merge, e.g. week wrap).
 * @param is_double_box_anchor True when `fullDate` is in `double_box_dates` / dayConfig.isDoubleBox.
 */
export function buildCalendarGridCellsWithDoubleBoxSpans(
  enriched: EnrichCalendarDayInput[],
  consumed_by_double_box: Set<string>,
  is_double_box_anchor: (full_date: string) => boolean,
): CalendarGridCellData[] {
  if (enriched.length === 0) {
    return [];
  }

  const skip = new Set<number>();
  const out: CalendarGridCellData[] = [];

  for (let i = 0; i < enriched.length; i++) {
    if (skip.has(i)) {
      continue;
    }
    const item = enriched[i]!;
    const { day, index, visual, disabled } = item;

    if (day.date === null || !day.fullDate) {
      out.push({ day, index, visual, disabled, col_span: 1 });
      continue;
    }

    const is_anchor = is_double_box_anchor(day.fullDate);
    if (!is_anchor) {
      // Consumed follow-up still rendered when not merged into a span (e.g. week wrap).
      out.push({
        day: { ...day, isDoubleBox: false },
        index,
        visual,
        disabled: disabled || consumed_by_double_box.has(day.fullDate),
        col_span: 1,
        is_double_box_span: false,
      });
      continue;
    }

    const next_ymd = nextCalendarYmd(day.fullDate);
    const week_row = Math.floor(i / 7);
    let pair_i: number | null = null;

    if (next_ymd) {
      for (let j = i + 1; j < enriched.length && Math.floor(j / 7) === week_row; j++) {
        const cand = enriched[j];
        if (!cand || cand.day.date == null || !cand.day.fullDate) {
          continue;
        }
        if (cand.day.fullDate !== next_ymd) {
          break;
        }
        // ALWAYS merge with the immediate next calendar day in this week row.
        // Do not gate on consumed / day-off / available — see file header.
        pair_i = j;
        break;
      }
    }

    if (pair_i != null) {
      const next = enriched[pair_i]!;
      skip.add(pair_i);
      out.push({
        day: { ...day, isDoubleBox: true },
        index,
        visual,
        disabled,
        col_span: 2,
        is_double_box_span: true,
        double_box_pair_day: next.day.date,
        double_box_pair_date: next.day.fullDate,
      });
      continue;
    }

    // Anchor only (pair wraps week / missing from this month grid) — still show ×2 badge.
    out.push({
      day: { ...day, isDoubleBox: true },
      index,
      visual,
      disabled,
      col_span: 1,
      is_double_box_span: true,
    });
  }

  return out;
}
